// ============================================================================
// EXPENSE VOUCHER SERVICE — Phase G
// ============================================================================

import { get, post, put, del } from '../client/axiosClient';
import { expenseEndpoints, EXPENSE_BASE_PATH } from '../endpoints/expense.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type { ApiResponse, EmptyResponse, PaginatedResponse, ID } from '../types';
import type {
  ApportionmentProposal,
  ComputeApportionmentRequest,
  CreateExpenseRequest,
  Expense,
  ExpenseAllocationView,
  ExpenseListItem,
  ExpenseSearchRequest,
  UpdateExpenseRequest,
} from '../types/expense.types';

const toPaginated = <T>(raw: any): PaginatedResponse<T> => {
  const paged = raw?.data ?? {};
  return {
    success: raw?.success ?? false,
    message: raw?.message,
    data: paged.items ?? [],
    pagination: {
      currentPage: paged.page ?? 1,
      pageSize: paged.pageSize ?? 25,
      totalPages: paged.totalPages ?? 0,
      totalItems: paged.totalCount ?? 0,
      hasNextPage: paged.hasNext ?? false,
      hasPreviousPage: paged.hasPrevious ?? false,
    },
  };
};

class ExpenseService {
  async getAll(params?: ExpenseSearchRequest): Promise<PaginatedResponse<ExpenseListItem>> {
    const endpoint = params
      ? buildEndpointWithQuery(EXPENSE_BASE_PATH, params as Record<string, any>)
      : expenseEndpoints.list();
    const res = await get<any>(endpoint);
    return toPaginated<ExpenseListItem>(res.data);
  }

  async getById(id: ID): Promise<Expense | null> {
    const res = await get<ApiResponse<Expense>>(expenseEndpoints.getById(id));
    return res.data?.data ?? null;
  }

  async create(req: CreateExpenseRequest): Promise<Expense> {
    const res = await post<ApiResponse<Expense>>(expenseEndpoints.create(), req);
    return res.data.data!;
  }

  async update(id: ID, req: UpdateExpenseRequest): Promise<Expense> {
    const res = await put<ApiResponse<Expense>>(expenseEndpoints.update(id), req);
    return res.data.data!;
  }

  async delete(id: ID): Promise<EmptyResponse> {
    const res = await del<EmptyResponse>(expenseEndpoints.delete(id));
    return res.data;
  }

  async computeApportionment(req: ComputeApportionmentRequest): Promise<ApportionmentProposal> {
    const res = await post<ApiResponse<ApportionmentProposal>>(expenseEndpoints.computeApportionment(), req);
    return res.data.data!;
  }

  async getByCase(caseId: ID): Promise<ExpenseAllocationView[]> {
    const res = await get<ApiResponse<ExpenseAllocationView[]>>(expenseEndpoints.byCase(caseId));
    return res.data?.data ?? [];
  }

  async getByCaseGroup(caseGroupId: ID): Promise<ExpenseAllocationView[]> {
    const res = await get<ApiResponse<ExpenseAllocationView[]>>(expenseEndpoints.byCaseGroup(caseGroupId));
    return res.data?.data ?? [];
  }
}

export const expenseService = new ExpenseService();
export default expenseService;
